import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';
import api from '../services/api';
import ImageCapture from '../components/ImageCapture';
import IdentityVerifyNotice from '../components/IdentityVerifyNotice';
import './Login.css';
import './Register.css';

function Register() {
    const [form, setForm] = useState({
        cid: '',
        fullname: '',
        phone: '',
        department: '',
        email: '',
        password: '',
        confirmPassword: '',
        note: ''
    });
    const [idCardFile, setIdCardFile] = useState(null);
    const [idCardPreview, setIdCardPreview] = useState('');
    const [faceFile, setFaceFile] = useState(null);       // รูปถ่ายตนเองถือบัตรประชาชน
    const [facePreview, setFacePreview] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const { appName, requireFacePhoto, photoCheckEnabled } = useBranding();
    const navigate = useNavigate();
    const idCardInputRef = useRef(null);
    // refs สำหรับเลื่อนไปหา field ที่กรอกไม่ครบตอนกดส่ง
    const cidRef = useRef(null);
    const fullnameRef = useRef(null);
    const phoneRef = useRef(null);
    const idCardSectionRef = useRef(null);
    const faceSectionRef = useRef(null);
    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const confirmRef = useRef(null);

    // เลื่อนหน้าจอไปหา field + โฟกัสให้ (ใช้ตอน validate ไม่ผ่าน)
    const scrollToField = (ref) => {
        const el = ref && ref.current;
        if (!el) return;
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { el.scrollIntoView(); }
        if (typeof el.focus === 'function') {
            setTimeout(() => { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }, 350);
        }
    };

    // Email verification (ลงทะเบียน)
    const [emailCode, setEmailCode] = useState('');
    const [emailCodeSent, setEmailCodeSent] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [emailMsg, setEmailMsg] = useState({ type: '', text: '' });

    const handleChange = (field, value) => {
        if (field === 'cid') {
            value = value.replace(/\D/g, '').slice(0, 13);
        }
        if (field === 'phone') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }
        // เปลี่ยนอีเมล → รีเซ็ตสถานะยืนยัน
        if (field === 'email') {
            setEmailCodeSent(false);
            setEmailVerified(false);
            setEmailCode('');
            setEmailMsg({ type: '', text: '' });
        }
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const sendEmailCode = async () => {
        const email = form.email.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailMsg({ type: 'error', text: 'กรุณากรอกอีเมลให้ถูกต้องก่อน' });
            return;
        }
        setSendingCode(true);
        setEmailMsg({ type: '', text: '' });
        try {
            const res = await api.post('/auth/register/send-code', { email });
            setEmailCodeSent(true);
            setEmailMsg({ type: 'success', text: res.data.message || 'ส่งรหัสยืนยันแล้ว' });
        } catch (err) {
            setEmailMsg({ type: 'error', text: err.response?.data?.error || 'ส่งรหัสไม่สำเร็จ' });
        } finally {
            setSendingCode(false);
        }
    };

    const verifyEmailCode = async () => {
        const email = form.email.trim().toLowerCase();
        if (!/^\d{6}$/.test(emailCode.trim())) {
            setEmailMsg({ type: 'error', text: 'กรุณากรอกรหัสยืนยัน 6 หลัก' });
            return;
        }
        setVerifyingCode(true);
        setEmailMsg({ type: '', text: '' });
        try {
            await api.post('/auth/register/verify-code', { email, code: emailCode.trim() });
            setEmailVerified(true);
            setEmailMsg({ type: 'success', text: 'ยืนยันอีเมลสำเร็จ' });
        } catch (err) {
            setEmailMsg({ type: 'error', text: err.response?.data?.error || 'ยืนยันไม่สำเร็จ' });
        } finally {
            setVerifyingCode(false);
        }
    };

    const handleIdCardSelect = (file) => {
        if (!file) return;
        // ยอมรับชนิดรูปทั่วไป (ถ่ายจากกล้องจะเป็น jpeg) — ถ้าว่างชนิด ก็ปล่อยผ่านให้ backend แปลง
        if (file.type && !['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/heic', 'image/heif'].includes(file.type)) {
            setError('รองรับเฉพาะรูปภาพ (JPG, PNG, WEBP, HEIC)');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setError('รูปบัตรต้องไม่เกิน 8MB');
            return;
        }
        setError('');
        setIdCardFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setIdCardPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const clearIdCard = () => {
        setIdCardFile(null);
        setIdCardPreview('');
        if (idCardInputRef.current) idCardInputRef.current.value = '';
    };

    // รูปถ่ายตนเองถือบัตรประชาชน (เห็นหน้า + ข้อมูลบัตรชัดเจน)
    const handleFaceSelect = (file) => {
        if (!file) return;
        if (file.type && !['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/heic', 'image/heif'].includes(file.type)) {
            setError('รองรับเฉพาะรูปภาพ (JPG, PNG, WEBP, HEIC)');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setError('รูปถ่ายต้องไม่เกิน 8MB');
            return;
        }
        setError('');
        setFaceFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setFacePreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const clearFace = () => {
        setFaceFile(null);
        setFacePreview('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!/^\d{13}$/.test(form.cid)) {
            setError('กรุณากรอกเลขบัตรประชาชนให้ครบ 13 หลัก');
            scrollToField(cidRef);
            return;
        }
        if (!form.fullname.trim()) {
            setError('กรุณากรอกชื่อ-นามสกุล');
            scrollToField(fullnameRef);
            return;
        }
        if (form.phone.replace(/\D/g, '').length < 9 || form.phone.replace(/\D/g, '').length > 10) {
            setError('กรุณากรอกเบอร์โทรติดต่อกลับให้ถูกต้อง (9-10 หลัก)');
            scrollToField(phoneRef);
            return;
        }
        if (requireFacePhoto && !faceFile) {
            setError('กรุณาถ่ายรูปตนเองถือบัตรประชาชน ให้เห็นใบหน้าและข้อมูลบัตรชัดเจน');
            scrollToField(faceSectionRef);
            return;
        }
        if (!idCardFile) {
            setError('กรุณาถ่าย/แนบรูปบัตรประจำตัวประชาชน (เฉพาะบัตร)');
            scrollToField(idCardSectionRef);
            return;
        }
        if (!form.email.trim()) {
            setError('กรุณากรอกอีเมล (จำเป็น เพื่อใช้รับการแจ้งเตือนและเอกสาร)');
            scrollToField(emailRef);
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            setError('รูปแบบอีเมลไม่ถูกต้อง');
            scrollToField(emailRef);
            return;
        }
        if (!emailVerified) {
            setError('กรุณายืนยันอีเมลด้วยรหัสที่ส่งไปก่อนส่งคำขอ');
            scrollToField(emailRef);
            return;
        }
        if (form.password.length < 6) {
            setError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร');
            scrollToField(passwordRef);
            return;
        }
        if (form.password !== form.confirmPassword) {
            setError('รหัสผ่านยืนยันไม่ตรงกัน');
            scrollToField(confirmRef);
            return;
        }

        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('cid', form.cid);
            fd.append('fullname', form.fullname.trim());
            fd.append('phone', form.phone.trim());
            fd.append('department', form.department.trim());
            fd.append('email', form.email.trim());
            fd.append('password', form.password);
            fd.append('note', form.note.trim());
            fd.append('id_card', idCardFile);
            if (faceFile) fd.append('face_image', faceFile);

            const res = await api.post('/auth/register-request', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSuccess(res.data.message || 'ส่งคำขอเรียบร้อยแล้ว');
            setForm({ cid: '', fullname: '', phone: '', department: '', email: '', password: '', confirmPassword: '', note: '' });
            setEmailCodeSent(false);
            setEmailVerified(false);
            setEmailCode('');
            setEmailMsg({ type: '', text: '' });
            clearIdCard();
            clearFace();
            setTimeout(() => navigate('/login'), 4000);
        } catch (err) {
            setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการส่งคำขอ');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card register-card">
                    <div className="login-header">
                        <div className="login-logo">
                            <img
                                src={`${process.env.PUBLIC_URL}/icon-512.png`}
                                alt={appName}
                                className="login-logo-icon"
                            />
                            <h1 className="login-title">{appName}</h1>
                        </div>
                        <p className="login-subtitle">ลงทะเบียนขอเปิดบัญชี</p>
                        <p className="login-hospital">โรงพยาบาลศรีสะเกษ</p>
                    </div>

                    {success ? (
                        <div className="register-success">
                            <svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            <h3>ส่งคำขอเรียบร้อย</h3>
                            <p>{success}</p>
                            <p className="register-redirect">กำลังกลับไปหน้าเข้าสู่ระบบ...</p>
                            <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
                                <Link to="/check-status" className="steam-btn steam-btn-secondary">
                                    ตรวจสอบสถานะบัญชี
                                </Link>
                                <Link to="/login" className="steam-btn steam-btn-primary">
                                    ไปหน้าเข้าสู่ระบบ
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="login-form">
                            {error && (
                                <div className="alert alert-error">
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                    </svg>
                                    {error}
                                </div>
                            )}

                            <div className="form-group">
                                <label className="form-label">เลขบัตรประชาชน <span className="req">*</span></label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    ref={cidRef}
                                    className="steam-input"
                                    value={form.cid}
                                    onChange={(e) => handleChange('cid', e.target.value)}
                                    placeholder="เลขบัตรประชาชน 13 หลัก"
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">ชื่อ-นามสกุล <span className="req">*</span></label>
                                <input
                                    type="text"
                                    ref={fullnameRef}
                                    className="steam-input"
                                    value={form.fullname}
                                    onChange={(e) => handleChange('fullname', e.target.value)}
                                    placeholder="เช่น นายสมชาย ใจดี"
                                    maxLength="150"
                                    required
                                />
                            </div>

                            {/* === คำชี้แจงการยืนยันตัวตน (ป้องกันการแอบอ้างสิทธิ์) === */}
                            <IdentityVerifyNotice requireFacePhoto={requireFacePhoto} />

                            {/* === รูปถ่ายตนเองถือบัตรประชาชน (บังคับเฉพาะโหมด face_card — ตั้งค่าในหน้าตั้งค่าระบบ) === */}
                            {requireFacePhoto && (
                            <div className="form-group" ref={faceSectionRef}>
                                <label className="form-label">
                                    1) รูปถ่ายตนเองถือบัตรประชาชน <span className="req">*</span>
                                </label>
                                <div className="idcard-instruction">
                                    ถือบัตรไว้ระดับอกใต้ใบหน้า <strong>หันด้านหน้าบัตร (ด้านที่มีรูปหน้า) เข้ากล้อง</strong> ถ่ายในที่แสงสว่าง —
                                    เห็น<strong>ใบหน้าชัดเจน</strong>และ<strong>ข้อมูลในบัตรอ่านได้</strong> (ตอนถ่ายจะมีกรอบช่วยจัดตำแหน่งให้)
                                </div>
                                {facePreview && (
                                    <div className="idcard-preview">
                                        <img src={facePreview} alt="ตัวอย่างรูปถือบัตรที่อัปโหลด" />
                                        <div className="idcard-preview-actions">
                                            <button type="button" className="steam-btn steam-btn-danger" onClick={clearFace}>
                                                ลบรูป
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <ImageCapture
                                    onSelect={handleFaceSelect}
                                    accept="image/*"
                                    facingMode="user"
                                    overlay="holdcard"
                                    check={photoCheckEnabled ? 'holdcard' : undefined}
                                    captureLabel={facePreview ? 'ถ่ายใหม่' : 'ถ่ายรูปถือบัตร'}
                                    browseLabel="เลือกรูปจากเครื่อง"
                                />
                            </div>
                            )}

                            {/* === ถ่ายรูปบัตรประชาชน (บังคับ) === */}
                            <div className="form-group" ref={idCardSectionRef}>
                                <label className="form-label">
                                    {requireFacePhoto ? '2) ' : ''}รูปด้านหน้าบัตรประจำตัวประชาชน (เฉพาะบัตร) <span className="req">*</span>
                                </label>

                                <div className="idcard-instruction">
                                    <strong>ถ่าย "ด้านหน้าบัตร" เท่านั้น</strong> — ด้านที่มีรูปหน้า เลขบัตร 13 หลัก และชื่อ-นามสกุล
                                    (❌ ไม่ใช่ด้านหลังบัตร) ถ่ายให้ชัด ไม่เบลอ ไม่มีแสงสะท้อนบังตัวหนังสือ
                                </div>

                                {/* mockup ตัวอย่าง */}
                                {!idCardPreview && (
                                    <div className="idcard-mockup" aria-hidden="true">
                                        <div className="idcard-mock-card">
                                            <div className="idcard-mock-head">
                                                <span className="idcard-mock-flag" />
                                                <span className="idcard-mock-title">บัตรประจำตัวประชาชน</span>
                                            </div>
                                            <div className="idcard-mock-body">
                                                <div className="idcard-mock-photo">
                                                    <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.6">
                                                        <circle cx="12" cy="8" r="4" />
                                                        <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                                                    </svg>
                                                </div>
                                                <div className="idcard-mock-lines">
                                                    <span className="idcard-mock-id">เลขประจำตัว 1 2345 67890 12 3</span>
                                                    <span className="line w80" />
                                                    <span className="line w60" />
                                                    <span className="line w70" />
                                                </div>
                                            </div>
                                            <div className="idcard-mock-tag">ตัวอย่าง</div>
                                        </div>
                                        <div className="idcard-mock-caption">
                                            ถ่าย "ด้านหน้าบัตร" ตามตัวอย่าง — จัดบัตรให้อยู่ในกรอบ ถ่ายตรงๆ ในที่แสงสว่างเพียงพอ
                                        </div>
                                    </div>
                                )}

                                {idCardPreview && (
                                    <div className="idcard-preview">
                                        <img src={idCardPreview} alt="ตัวอย่างบัตรที่อัปโหลด" />
                                        <div className="idcard-preview-actions">
                                            <button type="button" className="steam-btn steam-btn-danger" onClick={clearIdCard}>
                                                ลบรูป
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <ImageCapture
                                    onSelect={handleIdCardSelect}
                                    check={photoCheckEnabled ? 'idcard' : undefined}
                                    accept="image/*"
                                    captureLabel={idCardPreview ? 'ถ่ายใหม่' : 'ถ่ายรูปบัตร'}
                                    browseLabel="เลือกรูปบัตรจากเครื่อง"
                                />

                                <div className="idcard-privacy">
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    <span>
                                        เราจะเก็บรูปบัตรของคุณไว้อย่างปลอดภัย เข้าถึงได้เฉพาะเจ้าหน้าที่การเงินที่ได้รับอนุญาต
                                        เพื่อใช้ยืนยันตัวตนในการเปิดบัญชีเท่านั้น
                                    </span>
                                </div>
                            </div>

                            <div className="register-grid">
                                <div className="form-group" ref={phoneRef}>
                                    <label className="form-label">เบอร์โทรติดต่อกลับ (จำเป็น)</label>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        className="steam-input"
                                        value={form.phone}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                        placeholder="เช่น 0812345678"
                                        required
                                    />
                                    <small style={{ display: 'block', marginTop: 4, color: 'var(--steam-text-muted, #6b7280)', fontSize: 12 }}>
                                        เผื่อเจ้าหน้าที่ต้องโทรติดต่อกลับเพื่อยืนยันข้อมูล
                                    </small>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">หน่วยงาน/ตำแหน่ง</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        value={form.department}
                                        onChange={(e) => handleChange('department', e.target.value)}
                                        placeholder="เช่น งานการเงิน"
                                        maxLength="200"
                                    />
                                </div>
                            </div>

                            {/* อีเมล (บังคับ + ต้องยืนยันด้วยรหัส) */}
                            <div className="form-group">
                                <label className="form-label">
                                    อีเมล <span className="req">*</span>
                                    {emailVerified && (
                                        <>
                                            <span className="email-verified-tag">
                                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                                                ยืนยันแล้ว
                                            </span>
                                            <button
                                                type="button"
                                                className="email-edit-link"
                                                onClick={() => { setEmailVerified(false); setEmailCodeSent(false); setEmailCode(''); setEmailMsg({ type: '', text: '' }); }}
                                            >
                                                แก้ไขอีเมล
                                            </button>
                                        </>
                                    )}
                                </label>
                                <div className="email-verify-row">
                                    <input
                                        type="email"
                                        ref={emailRef}
                                        className="steam-input"
                                        value={form.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        placeholder="เช่น you@example.com"
                                        maxLength="255"
                                        disabled={emailVerified}
                                        required
                                    />
                                    {!emailVerified && (
                                        <button
                                            type="button"
                                            className="steam-btn steam-btn-secondary email-code-btn"
                                            onClick={sendEmailCode}
                                            disabled={sendingCode}
                                        >
                                            {sendingCode ? 'กำลังส่ง...' : (emailCodeSent ? 'ส่งรหัสใหม่' : 'ส่งรหัสยืนยัน')}
                                        </button>
                                    )}
                                </div>
                                <span className="form-hint-inline">
                                    จำเป็นต้องยืนยันอีเมล เพราะระบบจะส่งการแจ้งเตือนและเอกสารไปทางอีเมลนี้
                                </span>

                                {/* ช่องกรอกรหัส */}
                                {emailCodeSent && !emailVerified && (
                                    <div className="email-verify-row" style={{ marginTop: 10 }}>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="steam-input email-code-input"
                                            value={emailCode}
                                            onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="กรอกรหัส 6 หลัก"
                                            maxLength="6"
                                        />
                                        <button
                                            type="button"
                                            className="steam-btn steam-btn-primary email-code-btn"
                                            onClick={verifyEmailCode}
                                            disabled={verifyingCode}
                                        >
                                            {verifyingCode ? 'กำลังตรวจ...' : 'ยืนยัน'}
                                        </button>
                                    </div>
                                )}

                                {emailMsg.text && (
                                    <div className={`email-verify-msg ${emailMsg.type}`}>{emailMsg.text}</div>
                                )}
                            </div>

                            <div className="register-grid">
                                <div className="form-group">
                                    <label className="form-label">ตั้งรหัสผ่าน <span className="req">*</span></label>
                                    <input
                                        type="password"
                                        ref={passwordRef}
                                        className="steam-input"
                                        value={form.password}
                                        onChange={(e) => handleChange('password', e.target.value)}
                                        placeholder="อย่างน้อย 6 ตัวอักษร"
                                        minLength="6"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">ยืนยันรหัสผ่าน <span className="req">*</span></label>
                                    <input
                                        type="password"
                                        ref={confirmRef}
                                        className="steam-input"
                                        value={form.confirmPassword}
                                        onChange={(e) => handleChange('confirmPassword', e.target.value)}
                                        placeholder="กรอกรหัสผ่านอีกครั้ง"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">หมายเหตุ/เหตุผล</label>
                                <textarea
                                    className="steam-input"
                                    value={form.note}
                                    onChange={(e) => handleChange('note', e.target.value)}
                                    placeholder="ข้อมูลเพิ่มเติม (ถ้ามี)"
                                    rows="2"
                                    maxLength="500"
                                />
                            </div>

                            <button
                                type="submit"
                                className="steam-btn steam-btn-primary login-btn"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
                                        กำลังส่งคำขอ...
                                    </>
                                ) : 'ส่งคำขอเปิดบัญชี'}
                            </button>
                        </form>
                    )}

                    <div className="login-footer">
                        <p>มีบัญชีอยู่แล้ว? <Link to="/login" className="register-link">เข้าสู่ระบบ</Link></p>
                        <p style={{ marginTop: '4px' }}>
                            <Link to="/check-status" className="register-link">ตรวจสอบสถานะบัญชี</Link>
                        </p>
                        <p style={{ fontSize: '12px', marginTop: '4px', color: '#9ca3af' }}>
                            เจ้าหน้าที่จะตรวจสอบและเปิดบัญชีให้ภายหลัง
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Register;
